<template>
  <div>
    <!-- View Mode Toggle -->
    <div v-if="hasProvinceData" class="view-toggle-container">
      <div class="view-toggle-wrapper">
        <Button
          :class="['view-toggle-btn', { 'active': viewMode === 'country' }]"
          @click="setViewMode('country')"
        >
          <Icon type="ios-globe" />
          <span class="btn-text">{{ $t('Country View') }}</span>
        </Button>
        <Button
          :class="['view-toggle-btn', { 'active': viewMode === 'province' }]"
          @click="setViewMode('province')"
        >
          <Icon type="ios-map" />
          <span class="btn-text">{{ $t('Province View') }}</span>
        </Button>
      </div>
    </div>

    <GmapMap
      ref="gmap"
      :center="gmapAttr.center"
      :zoom="gmapAttr.zoom"
      :options="gmapAttr.options"
      class="global-risk-map"
      map-type-id="terrain"
    >
      <template v-for='incident in incidents'>
        <gmap-marker
          v-for="incident_location in incident.incident_locations"
          :key="`incident-${incident_location.id}`"
          :position="{lat: incident_location.latitude, lng: incident_location.longitude}"
          :icon="markIt('GenericIncident')"
        />
      </template>
      <GmapGeoJson
        :geo-data="currentGeoData"
        :options="currentGeoOptions"
        @onClick="handleGeoClick"
        @onHover="handleGeoHover"
      />
    </GmapMap>
  </div>
</template>

<script>
    import {countries} from "../../../assets/countries";
    import {loadProvinces} from "@/helpers/provinces.helper";
    import GmapGeoJson from "@/components/common/GmapGeoJson"
    import {polyCenter, countryAlertColor, getMarker, riskStringToLevel} from "../../../helpers/marker.helper";
    import {getProvinceRisksQry} from "../../../services/countryInfo.service";
    import Vue from 'vue';

    export default {
        name: "GlobalRiskMap",
        components: {
            GmapGeoJson
        },
        props: {
            countryRisk: {
                type: Object,
                required: true
            },
            incidents: {
                type: Array,
                default: () => []
            },
            mode: {
                type: String,
                default: 'country',
                validator: (value) => ['country', 'province'].includes(value)
            }
        },
        data() {
            return {
                gmapAttr: {
                    center: {lat: 10, lng: 10},
                    zoom: 4,
                    options: Vue.config.gmapDefaultOptions
                },

                currentMarker: {
                    lat: 10,
                    lng: 10,
                },

                viewMode: this.mode,
                provinceGeoData: null,
                provinceRisks: {}, // Simple object: { "ProvinceName": "R4", "OtherProvince": "R3" }
                hoveredProvinceCode: null,
                countryGeoData: [],
                loadingProvinces: false
            }
        },
        computed: {
            /**
             * Check if province data is available
             * Show toggle if we have GeoJSON (API data is optional - will fallback to country risk)
             */
            hasProvinceData() {
                return this.provinceGeoData !== null;
            },

            /**
             * Get current GeoJSON data based on view mode
             */
            currentGeoData() {
                if (this.viewMode === 'province' && this.provinceGeoData) {
                    return [this.provinceGeoData];
                }
                return this.countryGeoData;
            },

            /**
             * Get current geo styling options based on view mode
             */
            currentGeoOptions() {
                if (this.viewMode === 'country') {
                    // Single color for entire country
                    return {
                        fillColor: countryAlertColor(this.countryRisk.global_risk),
                        strokeWeight: 1,
                        strokeColor: '#333',
                        fillOpacity: 0.6
                    };
                } else {
                    // Per-feature styling function for provinces
                    return (feature) => {
                        const provinceName = feature.getProperty('name_en') || feature.getProperty('name');
                        const riskString = this.provinceRisks[provinceName]; // API returns { "ProvinceName": "R4" }

                        // If no API data for this province, fallback to country's global_risk
                        const riskLevel = riskString ? riskStringToLevel(riskString) : this.countryRisk.global_risk;

                        return {
                            fillColor: countryAlertColor(riskLevel),
                            strokeWeight: 1,
                            strokeColor: '#333',
                            fillOpacity: this.hoveredProvinceCode === provinceName ? 0.9 : 0.6,
                            cursor: 'pointer'
                        };
                    };
                }
            }
        },
        watch: {
            countryRisk: {
                async handler(value) {
                    if (!value || !value.country_alpha3) return;

                    // Center map on country
                    const polygonCenter = polyCenter(value.country_alpha3);
                    this.gmapAttr.center.lat = polygonCenter[1];
                    this.gmapAttr.center.lng = polygonCenter[0];

                    // Load country GeoJSON
                    this.countryGeoData = [countries[value.country_alpha3]];

                    // Load province data (GeoJSON + risks)
                    // Use route param which should be ISO2 or ISO3 - provinces API uses ISO2
                    const countryCode = this.$route?.params?.country || value.country_alpha2 || value.country;
                    console.log('[GlobalRiskMap] Loading provinces for country:', countryCode);
                    await this.loadProvinceData(countryCode);
                },
                deep: true,
                immediate: true
            },

            viewMode(newMode) {
                this.$emit('mode-changed', newMode);
            },

            mode(newMode) {
                this.viewMode = newMode;
            }
        },
        methods: {
            markIt(marker) {
                return getMarker(marker);
            },

            /**
             * Load province data (both GeoJSON and risks) for a country
             * @param {string} iso2Code - ISO2 country code (e.g., "AF", "MA")
             */
            async loadProvinceData(iso2Code, skipGeoData = false, bustCache = false) {
                if (!iso2Code) return;

                this.loadingProvinces = true;

                try {
                    // Load GeoJSON and API data in parallel
                    // Skip GeoJSON reload if we're just refreshing API data
                    const promises = [];

                    if (!skipGeoData) {
                        promises.push(loadProvinces(iso2Code).catch(() => null));
                    } else {
                        promises.push(Promise.resolve(this.provinceGeoData));
                    }

                    promises.push(
                        getProvinceRisksQry(iso2Code, bustCache).catch(err => {
                            // 404 is OK - means no province risks in DB yet, we'll use country risk as fallback
                            if (err.response?.status === 404) {
                                console.log(`[GlobalRiskMap] No province risks in DB for ${iso2Code}, will use country risk as fallback`);
                                return null;
                            }
                            throw err;
                        })
                    );

                    const [geoData, apiResponse] = await Promise.all(promises);

                    if (!skipGeoData) {
                        this.provinceGeoData = geoData;
                    }
                    this.provinceRisks = apiResponse?.data || {};

                    console.log(`[GlobalRiskMap] Province data loaded for ${iso2Code}:`, {
                        geoJsonProvinces: this.provinceGeoData?.features?.length || 0,
                        apiProvinces: Object.keys(this.provinceRisks).length,
                        fallbackToCountryRisk: this.provinceGeoData && Object.keys(this.provinceRisks).length === 0
                    });
                } catch (error) {
                    console.error(`[GlobalRiskMap] Failed to load province data:`, error);
                    if (!skipGeoData) {
                        this.provinceGeoData = null;
                    }
                    this.provinceRisks = {};
                } finally {
                    this.loadingProvinces = false;
                }
            },

            /**
             * Set view mode (country or province)
             */
            setViewMode(mode) {
                this.viewMode = mode;
            },

            /**
             * Handle click on geographic feature
             */
            handleGeoClick(event) {
                if (this.viewMode === 'province' && event.feature) {
                    const provinceName = event.feature.getProperty('name_en') ||
                                        event.feature.getProperty('name');

                    if (provinceName) {
                        const riskString = this.provinceRisks[provinceName];
                        // If no API data, use country risk as fallback
                        const fallbackRiskString = riskString || `R${this.countryRisk.global_risk}`;

                        this.$emit('province-clicked', {
                            provinceName,
                            riskString: fallbackRiskString,
                            riskLevel: this.riskStringToLevel(fallbackRiskString),
                            allProvinceRisks: this.provinceRisks
                        });
                    }
                }
            },

            /**
             * Handle province risk update from modal
             * Reload data from API to ensure sync
             */
            async handleProvinceRiskUpdated(updatedProvinceRisks) {
                console.log('[GlobalRiskMap] Province risk updated, reloading data from API...');

                // Immediately update UI with optimistic data
                this.provinceRisks = updatedProvinceRisks;
                this.$emit('province-risks-updated', updatedProvinceRisks);

                // Reload from API to ensure we have latest data from backend
                // Skip GeoData reload since it doesn't change
                // Bust cache to force fresh data
                const countryCode = this.$route?.params?.country || this.countryRisk.country_alpha2 || this.countryRisk.country;

                try {
                    await this.loadProvinceData(countryCode, true, true); // skipGeoData=true, bustCache=true
                    console.log('[GlobalRiskMap] ✅ Province data reloaded successfully');
                } catch (error) {
                    console.error('[GlobalRiskMap] ❌ Failed to reload province data:', error);
                }
            },

            riskStringToLevel(riskString) {
                return riskStringToLevel(riskString);
            },

            /**
             * Handle hover on geographic feature
             */
            handleGeoHover(event, isOut) {
                if (this.viewMode === 'province' && event.feature) {
                    if (isOut) {
                        this.hoveredProvinceCode = null;
                    } else {
                        this.hoveredProvinceCode = event.feature.getProperty('name_en') ||
                                                  event.feature.getProperty('name');
                    }
                }
            }
        }
    }
</script>

<style scoped>
    .global-risk-map {
        margin: 20px 0;
        background: #25C285;
        height: 400px;
    }

    .view-toggle-container {
        display: flex;
        justify-content: center;
        margin: 10px;
        margin-top: 20px;
    }

    .view-toggle-wrapper {
        display: inline-flex;
        background: #f5f7fa;
        border-radius: 4px;
        /* padding: 4px; */
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        display: flex;
        gap: 4px;
    }

    .view-toggle-btn {
        border: none !important;
        background: transparent !important;
        color: #515a6e !important;
        font-weight: 500;
        padding: 8px 20px !important;
        margin: 0 !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 140px;
        justify-content: center;
    }

    .view-toggle-btn:hover {
        background: var(--primary-color) !important;
    }

    .view-toggle-btn.active {
        background: var(--sidebar-color) !important;
        color: #ffffff !important;
        box-shadow: 0 2px 4px rgba(45, 140, 240, 0.3);
    }

    .view-toggle-btn >>> .ivu-icon {
        font-size: 16px;
    }

    .btn-text {
        font-size: 12px;
    }
</style>
